import { Alert, Modal, Box, Button, SpaceBetween } from '@amzn/awsui-components-console';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import shortUUID from 'short-uuid';

import { useExcludeUserMutation } from '@/api/generated';
import { useGetClassroom } from '@/api/hooks';
import { useAppNotifications } from '@/utils/appNotifications';

import { messages } from './RemoveStudentModal.messages';

export type RemoveStudentModalProps = {
    isVisible: boolean;
    isVisibleSet: (isVisible: boolean) => void;
    email?: string;
    isRemovingAcceptedStudent?: boolean;
};

const RemoveStudentModal = ({
    isVisible,
    isVisibleSet,
    email,
    isRemovingAcceptedStudent,
}: RemoveStudentModalProps) => {
    const { formatMessage } = useIntl();
    const { data, updateCache } = useGetClassroom();
    const { addNotification } = useAppNotifications();
    const [errMsg, errMsgSet] = useState<null | string>(null);
    const { isLoading, mutate: callRemoveStudent } = useExcludeUserMutation({
        onMutate: () => {
            errMsgSet(null);
        },
        onSuccess: () => {
            updateCache({ studentRosterExclusion: email });
            addNotification({
                type: 'success',
                content: formatMessage(messages.studentWasRemovedAlert, { email }),
            });
            isVisibleSet(false);
        },
        onError: () => {
            errMsgSet(formatMessage(messages.studentWasNotRemovedAlert, { email }));
        },
    });

    const { classroomId, providerArn } = data?.getClassroom.classroom ?? {};

    if (!classroomId || !email) return null;

    const onRemove = () => {
        callRemoveStudent({
            input: {
                classroomId,
                providerArn,
                user: { email },
                relationship: 'student',
                clientRequestToken: shortUUID.generate(),
            },
        });
    };

    return (
        <Modal
            onDismiss={() => isVisibleSet(false)}
            closeAriaLabel={formatMessage(messages.closeModalButtonLabel)}
            data-testid='remove-student-modal'
            visible={isVisible}
            header={formatMessage(messages.headerLabel)}
            footer={
                <Box float='right'>
                    <SpaceBetween direction='horizontal' size='xs'>
                        <Button onClick={() => isVisibleSet(false)} variant='normal'>
                            {formatMessage(messages.closeModalButtonText)}
                        </Button>
                        <Button
                            data-testid='confirm-remove'
                            onClick={onRemove}
                            loading={isLoading}
                            variant='primary'
                        >
                            {formatMessage(messages.removeStudentModalButtonText)}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction='vertical' size='s'>
                {isRemovingAcceptedStudent && (
                    <Alert type='warning'>
                        {formatMessage(messages.removeAcceptedStudentAlert, {
                            email,
                            bold: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
                        })}
                    </Alert>
                )}
                {data?.getClassroom.classroom?.learningActivityID && (
                    <Alert type='warning' data-testid='roster-discrepancy-warning'>
                        {formatMessage(messages.rosterDiscrepancyWarning)}
                    </Alert>
                )}
                <Box tagOverride='p'>
                    {formatMessage(messages.removeStudentModalBody, { email })}
                </Box>
                {errMsg ? <Alert type='error'>{errMsg}</Alert> : null}
            </SpaceBetween>
        </Modal>
    );
};

export default RemoveStudentModal;
